import { Form, Formik } from "formik";
import { useCurrentCompany } from "hooks/auth";
import { Documentation } from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Documentation";
import { FlexibleQuoteFile } from "pages/RFQSubmission/RFQPreview/FlexibleQuoteFile";
import QuoteFile from "pages/RFQSubmission/RFQPreview/QuoteFile";
import React from "react";
import { Spinner } from "@chakra-ui/react";
import { RFX_TABS } from "utils/constants";

import RfxViewTabs from "newui/features/Rfx/components/RfxViewTabs";
import type { QuoteTableColumn } from "newui/features/Rfx/components/QuoteTable/quote-table.types";
import { DynamicQuoteTable, SummaryTable } from "newui/features/Rfx/components/QuoteTable";
import type { TotalRow } from "newui/features/Rfx/components/QuoteTable";
import { TOTAL_ROWS } from "newui/features/Rfx/components/QuoteTable";
import { useProcurerQuoteTables, useSummaryTables } from "newui/features/Rfx/Creation/hooks/useProcurerQuotes";
import type { RfxQuoteTable } from "newui/features/Rfx/Submission/types/quote.type";
import ReturnableSchedule from "newui/features/Rfx/Submission/components/ReturnableSchedule";
import { RfxDetails } from "newui/features/Rfx/Submission/components/ViewSubmission";
import QuoteInstructions from "newui/features/Rfx/Submission/components/ViewSubmission/QuoteInstructions";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";

import { PreviewSuppliers } from "./PreviewSuppliers";
import { RfxTab } from "newui/features/Rfx/types/component-types";

interface PreviewRfxDraftProps {
	[key: string]: any;
	documentCategories: { label: string; value: number }[];
	isDraftPreview?: boolean;
	isTemplate?: boolean;
	previewTab?: RfxTab;
	rfxData: Rfx;
	rfxTypes: any[];
}

const PreviewRfxDraft: React.FC<PreviewRfxDraftProps> = ({
	rfxTypes,
	documentCategories,
	isDraftPreview = false,
	isTemplate = false,
	rfxData,
	previewTab = RFX_TABS.DOCUMENTATION,
	...props
}) => {
	const [currentTab, setCurrentTab] = React.useState(previewTab);

	const company = useCurrentCompany();

	const rfxId = rfxData?.id ?? null;
	const { data: quoteTables, isLoading: isLoadingQuotes } = useProcurerQuoteTables(
		currentTab === RFX_TABS.QUOTE ? rfxId : null,
		isTemplate ? "rfx-template" : "rfx"
	);
	const { data: summaryTables } = useSummaryTables(
		currentTab === RFX_TABS.QUOTE ? rfxId : null,
		isTemplate ? "rfx-template" : "rfx"
	);

	const quoteTableNames = React.useMemo(() => {
		const map: Record<number, string> = {};
		(quoteTables || []).forEach((qt) => {
			map[qt.id] = qt.name || `Table ${qt.id}`;
		});
		return map;
	}, [quoteTables]);

	const mapColumnsForView = (columns: RfxQuoteTable['columns']): QuoteTableColumn[] =>
		(columns || []).map((col) => ({
			id: col.id,
			columnType: col.columnType,
			label: col.name,
			sortOrder: col.sortOrder,
			isDefault: false,
			isSupplierEditable: col.isSupplierEditable,
		}));

	const buildTotals = (): TotalRow[] =>
		TOTAL_ROWS.map((row) => ({
			id: row.id,
			name: row.label,
			color: row.color,
			value: 0,
		}));

	const documentAttachments = rfxData?.documentationDocument.reduce(
		(final, doc) => {
			const category = documentCategories.find(
				(docs) => docs.value === doc.categoryId
			);
			const haveIndex = final.findIndex(
				(docs) => docs.category === category?.label
			);
			if (haveIndex >= 0) {
				const docObj = doc?.fileName
					? {
						name: doc?.fileName,
						file_size: doc?.fileSize,
						path: doc?.documentPath,
					}
					: doc;
				final[haveIndex].documents = [...final[haveIndex].documents, docObj];
				return final;
			} else {
				const docObj = doc?.fileName
					? {
						name: doc?.fileName,
						file_size: doc?.fileSize,
						path: doc?.documentPath,
					}
					: doc;
				final.push({
					category: category?.label,
					documents: [docObj],
				});
				return final;
			}
		},
		[]
	);

	const clientName =
		rfxData?.client?.length > 0 ? rfxData?.client : company?.name;

	return (
		<>
			<RfxDetails
				rfxData={rfxData}
				isTemplate={isTemplate}
			/>
			<div className="my-6">
				<RfxViewTabs
					setCurrentTab={setCurrentTab}
					currentTab={currentTab}
					rfxData={rfxData}
				/>
			</div>
			<div>
				{currentTab === RFX_TABS.QUESTIONNAIRE && (
					<Formik
						initialValues={[]}
						onSubmit={null}
					>
						<Form>
							<ReturnableSchedule
								hasKeyPersonnel={true}
								hasPastProjects={true}
								rfxInviteId={null}
								returnableSchedule={rfxData?.questionnaire}
								rfxAnswers={null}
								isReturnableSchedulesTypeFile={rfxData?.returnableScheduleType}
								companyName={rfxData?.companyOwner?.name}
								templateReturnableScheduleFile={rfxData?.returnableScheduleFile}
								disabled={true}
								isPreview={isDraftPreview}
							/>
						</Form>
					</Formik>
				)}
				{currentTab === RFX_TABS.QUOTE && (
					<div className="flex flex-col gap-8">
						<QuoteInstructions
							quoteInstructions={rfxData?.quoteInstructions}
						/>
						{rfxData?.quoteType === "file" ? (
							<QuoteFile
								companyName={rfxData?.companyOwner?.name ?? company?.name}
								fileName={rfxData?.quoteFile?.file_name}
								quoteFilepath={rfxData?.quoteFile?.file_path}
								disabled={true}
								isPreview={isDraftPreview}
								rfxId={rfxData?.id}
								rfxInviteId={null}
								submissionQuoteFiles={null}
							/>
						) : isLoadingQuotes ? (
							<div className="flex items-center justify-center py-8">
								<Spinner size="lg" />
							</div>
						) : (
							<div className="flex flex-col gap-6">
								{quoteTables?.map((quoteTable) => (
									<div key={quoteTable.id}>
										<h3 className="mb-2 font-roboto text-lg font-semibold text-gray-900">
											{quoteTable.name}
										</h3>
										{quoteTable.description && (
											<p className="mb-2 text-sm text-gray-500">{quoteTable.description}</p>
										)}
										<DynamicQuoteTable
											columns={mapColumnsForView(quoteTable.columns)}
											includeTotalsRow={quoteTable.includeTotalsRow === true}
											items={quoteTable.rows || []}
											disabled={true}
											mode="view"
											totals={quoteTable.columns?.some((c) => c.columnType === "total") ? buildTotals() : []}
										/>
									</div>
								))}

								{summaryTables && summaryTables.length > 0 && (
									<div className="mt-4">
										<h3 className="mb-4 font-roboto text-lg font-semibold text-primary-900">
											Summary Tables
										</h3>
										{summaryTables.map((st) => (
											<SummaryTable
												key={st.id}
												categoryNames={st.value_column_names}
												includeColumnTotals={st.include_column_totals}
												mode="view"
												quoteTableIds={st.quote_table_ids}
												quoteTableNames={quoteTableNames}
												resourceType={isTemplate ? "rfx-template" : "rfx"}
												rfxId={rfxId!}
												summaryTableId={st.id}
												summaryTableName={st.name}
											/>
										))}
									</div>
								)}
							</div>
						)}
						{rfxData?.allow_flexible_quote && (
							<FlexibleQuoteFile
								rfqId={rfxData?.id}
								rfxInviteId={null}
								clientName={clientName}
								flexibleQuoteSavedFiles={null}
								disabled={true}
								isPreview={true}
							/>
						)}
					</div>
				)}
				{currentTab === RFX_TABS.DOCUMENTATION && (
					<Documentation
						documentAttachments={documentAttachments}
						documentDescription={rfxData.documentationDescription}
					/>
				)}
				{currentTab === RFX_TABS.SUPPLIERS && (
					<PreviewSuppliers
						isLoading={!rfxData}
						rfx={rfxData}
					/>
				)}
			</div>
		</>
	);
};

export default PreviewRfxDraft;
